/**
 * ObstacleFunctions.js
 *
 * This module provides utility functions for managing obstacles (like chimneys, vents, skylights)
 * in the SightFlight Designer application. It handles operations such as:
 *
 * 1. Deleting obstacles from segments (now undo + redo)
 * 2. Selecting and highlighting obstacles
 * 3. Deselecting obstacles and restoring their original appearance
 * 4. Copy/paste of obstacles (now undo + redo)
 *
 * These functions complement the ObstacleManager class by providing operations
 * that occur after obstacle creation, particularly for user interaction.
 */

import * as THREE from "three";
import { POLYGONS, VARS } from "../Global/variables";
import { sceneManager } from "../SetUps/SceneManager";
import { COLORS } from "../Global/materials";
import { addExtrude, setObstacleVisualColor } from "./Modifications";
import { addOffset, projectObstaclePointsToRoof } from "./Shapes";
import {
    enableObstacleVertexDrag,
    disableObstacleVertexDrag,
} from "./ObstacleDragControls";
import { removeSegmentDragControls } from "../Segments/DragControls";
import { createSegmentPlane } from "../Global/functions";
import { updateLinkedSegmentsAfterMove } from "./ObstacleManager";
import { pushUndoAction } from "../Global/UndoManager";
import { toast } from "../../lib/toast";
import { getVertices, polygonsOverlapOnPlane } from "../Panels/Helpers";
import { refreshPanelOverlapHighlights } from "../Panels/ValidationVisuals";
import { notifyIrradianceNeedsRecompute } from "../Shading/IrradianceEvents";

function forEachObstacleOnce(handler) {
    const seen = new Set();
    for (let segIndex = 0; segIndex < POLYGONS.length; segIndex++) {
        const segment = POLYGONS[segIndex];
        const obsList = segment?.obstructions;
        if (!Array.isArray(obsList)) continue;
        for (const obs of obsList) {
            if (!obs || seen.has(obs)) continue;
            seen.add(obs);
            handler(obs, segIndex);
        }
    }
}

export function reindexObstaclesAfterSegmentDelete(deletedIndex) {
    if (!Number.isInteger(deletedIndex)) return;

    forEachObstacleOnce((obs, segIndex) => {
        if (typeof obs.parentSegment === "number") {
            if (obs.parentSegment > deletedIndex) {
                obs.parentSegment -= 1;
            } else if (obs.parentSegment === deletedIndex) {
                obs.parentSegment = segIndex;
            }
        } else {
            obs.parentSegment = segIndex;
        }

        if (Array.isArray(obs.linkedSegments)) {
            const updated = [];
            for (const idx of obs.linkedSegments) {
                if (typeof idx !== "number") continue;
                if (idx === deletedIndex) continue;
                updated.push(idx > deletedIndex ? idx - 1 : idx);
            }
            const unique = Array.from(new Set(updated));
            obs.linkedSegments = unique.length
                ? unique
                : (typeof obs.parentSegment === "number" ? [obs.parentSegment] : []);
        } else if (typeof obs.parentSegment === "number") {
            obs.linkedSegments = [obs.parentSegment];
        }
    });
}

export function reindexObstaclesAfterSegmentInsert(insertIndex) {
    if (!Number.isInteger(insertIndex)) return;

    forEachObstacleOnce((obs, segIndex) => {
        if (typeof obs.parentSegment === "number") {
            if (obs.parentSegment >= insertIndex) {
                obs.parentSegment += 1;
            }
        } else {
            obs.parentSegment = segIndex >= insertIndex ? segIndex + 1 : segIndex;
        }

        if (Array.isArray(obs.linkedSegments)) {
            const updated = obs.linkedSegments
                .filter((idx) => typeof idx === "number")
                .map((idx) => (idx >= insertIndex ? idx + 1 : idx));
            const unique = Array.from(new Set(updated));
            obs.linkedSegments = unique.length
                ? unique
                : (typeof obs.parentSegment === "number" ? [obs.parentSegment] : []);
        } else if (typeof obs.parentSegment === "number") {
            obs.linkedSegments = [obs.parentSegment];
        }
    });
}

function getObstacleBoundaryPointsFromMesh(mesh, fallbackPoints) {
    if (mesh?.updateMatrixWorld) mesh.updateMatrixWorld(true);
    const storedOffsetOutline = mesh?.userData?.obstacleOffsetOutlineWorld;
    if (Array.isArray(storedOffsetOutline) && storedOffsetOutline.length >= 3) {
        return storedOffsetOutline.map((p) => p.clone());
    }
    const offset = mesh?.getObjectByName?.("offset");
    if (offset?.geometry?.attributes?.position) {
        if (offset.updateMatrixWorld) offset.updateMatrixWorld(true);
        return getVertices(offset);
    }
    if (Array.isArray(fallbackPoints)) return fallbackPoints;
    return [];
}

function obstacleOverlapsPanelsInSegment(mesh, fallbackPoints, segmentIndex) {
    const obstaclePoints = getObstacleBoundaryPointsFromMesh(mesh, fallbackPoints);
    if (!obstaclePoints.length) return false;

    const basePlane = typeof segmentIndex === "number" ? createSegmentPlane(segmentIndex) : null;
    const baseNormal = basePlane?.normal?.clone().normalize() || null;
    const planeTolerance = 0.05; // 5 cm

    const candidateSegments = [];
    for (let i = 0; i < POLYGONS.length; i++) {
        const seg = POLYGONS[i];
        if (!seg || !Array.isArray(seg.panels) || seg.panels.length === 0) continue;

        const plane = createSegmentPlane(i);
        if (!plane) continue;

        if (baseNormal) {
            const dot = Math.abs(baseNormal.dot(plane.normal.clone().normalize()));
            if (dot < 0.99) continue;
        }

        const dist = Math.abs(plane.distanceToPoint(obstaclePoints[0]));
        if (dist > planeTolerance) continue;

        candidateSegments.push({ index: i, plane });
    }

    if (!candidateSegments.length && typeof segmentIndex === "number") {
        const plane = createSegmentPlane(segmentIndex);
        if (plane) {
            candidateSegments.push({ index: segmentIndex, plane });
        }
    }

    for (const { index, plane } of candidateSegments) {
        const segment = POLYGONS[index];
        if (!segment || !Array.isArray(segment.panels) || segment.panels.length === 0) continue;
        const normal = plane.normal.clone().normalize();

        for (const panelData of segment.panels) {
            const panel = panelData?.panel;
            if (!panel) continue;
            if (panel.updateMatrixWorld) panel.updateMatrixWorld(true);
            const panelPoints = getVertices(panel);
            if (!panelPoints.length) continue;
            if (polygonsOverlapOnPlane(panelPoints, obstaclePoints, normal, panel.position)) {
                return true;
            }
        }
    }

    return false;
}

function disposeObstacleMesh(mesh) {
    if (!mesh) return;
    if (mesh.parent) {
        mesh.parent.remove(mesh);
    } else if (sceneManager.scene) {
        sceneManager.scene.remove(mesh);
    }
    mesh.traverse?.((obj) => {
        if (obj.geometry) obj.geometry.dispose();
        if (obj.material) obj.material.dispose();
    });
}

/**
 * Deletes an obstacle from a segment (now undo + redo via UndoManager)
 *
 * @param {number} segmentIndex - Index of the segment containing the obstacle
 * @param {number} obstacleIndex - Index of the obstacle to delete
 */
export function deleteObstacle(segmentIndex, obstacleIndex, options = {}) {
    const { setPolygons } = options;
    const selectedSegment = POLYGONS[segmentIndex];
    if (!selectedSegment) return;

    const selectedObs = selectedSegment.obstructions[obstacleIndex];
    if (!selectedObs) return;

    // Turn off vertex-drag mode so no more drag events fire for this obstacle.
    disableObstacleVertexDrag();

    // Snapshot obstacle data for undo/redo
    const parentSegmentIndex =
        typeof selectedObs.parentSegment === "number"
            ? selectedObs.parentSegment
            : segmentIndex;

    const cordsSnapshot = selectedObs.cords.map((p) => p.clone());
    const typeSnapshot = selectedObs.type;
    const heightSnapshot = selectedObs.height;
    const offsetSnapshot = selectedObs.offset;
    const isFilledSnapshot = selectedObs.isFilled === true;
    const colorSnapshot = selectedObs.ogColor || COLORS.red;
    const uiSnapshot = { type: typeSnapshot, height: heightSnapshot, offset: offsetSnapshot, isFilled: isFilledSnapshot };
    const uiIndexSnapshot = obstacleIndex;

    const obstacleRef = selectedObs;

    // Delete immediately (this is the actual user action)
    internalDeleteObstacle(obstacleRef);

    console.log(
        "🗑️ Deleted obstacle from segments (undoable):",
        obstacleRef.linkedSegments,
        POLYGONS
    );

    // Register undo/redo
    pushUndoAction({
        label: "Delete obstacle",
        undo: () => {
            internalRecreateObstacle(
                obstacleRef,
                cordsSnapshot,
                typeSnapshot,
                heightSnapshot,
                offsetSnapshot,
                isFilledSnapshot,
                colorSnapshot,
                parentSegmentIndex
            );
            if (typeof setPolygons === "function") {
                setPolygons((prev) => {
                    if (!prev || !prev[parentSegmentIndex]) return prev;
                    const next = [...prev];
                    const seg = { ...next[parentSegmentIndex] };
                    const obsList = Array.isArray(seg.obstructions) ? [...seg.obstructions] : [];
                    const insertIndex = Math.min(uiIndexSnapshot, obsList.length);
                    obsList.splice(insertIndex, 0, uiSnapshot);
                    seg.obstructions = obsList;
                    next[parentSegmentIndex] = seg;
                    return next;
                });
            }
        },
        redo: () => {
            internalDeleteObstacle(obstacleRef);
            if (typeof setPolygons === "function") {
                setPolygons((prev) => {
                    if (!prev || !prev[parentSegmentIndex]) return prev;
                    const next = [...prev];
                    const seg = { ...next[parentSegmentIndex] };
                    const obsList = Array.isArray(seg.obstructions) ? [...seg.obstructions] : [];
                    if (uiIndexSnapshot < 0 || uiIndexSnapshot >= obsList.length) return prev;
                    obsList.splice(uiIndexSnapshot, 1);
                    seg.obstructions = obsList;
                    next[parentSegmentIndex] = seg;
                    return next;
                });
            }
        },
    });
}

/**
 * Selects or deselects an obstacle
 *
 * @param {number} segmentIndexClicked - Segment index whose obstacle list you clicked in
 * @param {Object} selectedIndices - Current selection state { segmentIndex, obstacleIndex, panelIndex }
 * @param {number} index - Index of the obstacle in POLYGONS[segmentIndexClicked].obstructions
 * @param {Function} setSelectedIndices - Recoil setter for selection state
 */
export function selectObstacle(
    segmentIndexClicked,
    selectedIndices,
    index,
    setSelectedIndices,
    setPolygonDetails = null
) {
    const { segmentIndex: prevSegmentIndex, obstacleIndex: prevObstacleIndex } =
        selectedIndices;

    if (
        segmentIndexClicked === null ||
        segmentIndexClicked === undefined
    )
        return;

    const seg = POLYGONS[segmentIndexClicked];
    if (!seg || !seg.obstructions || seg.obstructions.length === 0) return;

    if (index < 0 || index >= seg.obstructions.length) return;

    const selectedObs = seg.obstructions[index];

    // Already selected → deselect
    if (
        prevSegmentIndex === segmentIndexClicked &&
        prevObstacleIndex === index
    ) {
        setSelectedIndices({
            segmentIndex: segmentIndexClicked,
            obstacleIndex: null,
            panelIndex: null,
        });

        deselectObstacle(selectedObs);
        disableObstacleVertexDrag();
        return;
    }

    // Deselect previously selected obstacle on ANY segment
    if (
        prevSegmentIndex !== null &&
        prevSegmentIndex !== undefined &&
        prevObstacleIndex !== null &&
        prevObstacleIndex !== undefined
    ) {
        const prevSeg = POLYGONS[prevSegmentIndex];
        if (
            prevSeg &&
            prevSeg.obstructions &&
            prevSeg.obstructions[prevObstacleIndex]
        ) {
            const prevObs = prevSeg.obstructions[prevObstacleIndex];
            deselectObstacle(prevObs);
        }
    }

    if (selectedObs?.mesh?.material?.color) {
        VARS.selectedObs = {
            originalColor: selectedObs.mesh.material.color.clone(),
        };
        setObstacleVisualColor(selectedObs.mesh, COLORS.yellow);
    }

    // Disable segment drag controls while an obstacle is selected.
    removeSegmentDragControls();

    setSelectedIndices({
        segmentIndex: segmentIndexClicked,
        obstacleIndex: index,
        panelIndex: null,
    });

    enableObstacleVertexDrag(segmentIndexClicked, index);
}

/**
 * Deselects an obstacle and restores its original appearance
 */
export function deselectObstacle(selectedObs) {
    if (!selectedObs || !selectedObs.mesh || !selectedObs.mesh.material) return;
    const color =
        typeof selectedObs.ogColor === "number" && Number.isFinite(selectedObs.ogColor)
            ? selectedObs.ogColor
            : COLORS.red;
    setObstacleVisualColor(selectedObs.mesh, color);
}

// Copy currently selected obstacle into a simple data clipboard
export function copySelectedObstacle(selectedIndices) {
    const { segmentIndex, obstacleIndex } = selectedIndices;
    if (segmentIndex === null || obstacleIndex === null) return;

    const obs = POLYGONS[segmentIndex]?.obstructions?.[obstacleIndex];
    if (!obs) return;

    VARS.copiedObstacle = {
        segmentIndex,
        type: obs.type,
        height: obs.height,
        offset: obs.offset,
        isFilled: obs.isFilled === true,
        ogColor: obs.ogColor || COLORS.red,
        cords: obs.cords.map((p) => p.clone()),
    };

    toast.success("Obstacle copied");
    console.log("📋 Copied obstacle:", VARS.copiedObstacle);
}

/**
 * Build a 2D XY polygon for a given segment from its edges.
 */
function getSegmentPolygon2D(segmentIndex) {
    const segment = POLYGONS[segmentIndex];
    if (!segment || !segment.edges || !segment.edges.length) return null;

    const segPoints2D = [];
    segment.edges.forEach((edge) => {
        if (edge.point1)
            segPoints2D.push(new THREE.Vector2(edge.point1.x, edge.point1.y));
        if (edge.point2)
            segPoints2D.push(new THREE.Vector2(edge.point2.x, edge.point2.y));
    });

    const uniquePoints = [];
    for (let p of segPoints2D) {
        if (!uniquePoints.some((u) => u.distanceTo(p) < 1e-6))
            uniquePoints.push(p);
    }
    if (!uniquePoints.length) return null;

    const center = uniquePoints
        .reduce((acc, p) => acc.add(p), new THREE.Vector2())
        .divideScalar(uniquePoints.length);

    uniquePoints.sort(
        (a, b) =>
            Math.atan2(a.y - center.y, a.x - center.x) -
            Math.atan2(b.y - center.y, b.x - center.x)
    );

    return uniquePoints;
}

/**
 * Point-in-polygon test (2D XY) using ray casting.
 */
function pointInPolygon2D(point, polygon) {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const xi = polygon[i].x,
            yi = polygon[i].y;
        const xj = polygon[j].x,
            yj = polygon[j].y;

        const intersect =
            yi > point.y !== yj > point.y &&
            point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi;

        if (intersect) inside = !inside;
    }
    return inside;
}

/**
 * Find which segment is under a given ray by intersecting its plane + XY polygon.
 *
 * Returns { segmentIndex, hitPoint } or null if none.
 */
function findSegmentUnderRay(ray, camera, fallbackPlaneIndex = null) {
    const origin = ray.origin.clone();
    let best = null;

    POLYGONS.forEach((seg, idx) => {
        if (!seg) return;

        const plane = createSegmentPlane(idx);
        if (!plane) return;

        const hit = new THREE.Vector3();
        if (!ray.intersectPlane(plane, hit)) return;

        const poly = getSegmentPolygon2D(idx);
        if (!poly) return;

        const p2d = new THREE.Vector2(hit.x, hit.y);
        if (!pointInPolygon2D(p2d, poly)) return;

        const dist = origin.distanceTo(hit);
        if (!best || dist < best.distance) {
            best = { segmentIndex: idx, hitPoint: hit.clone(), distance: dist };
        }
    });

    return best ? { segmentIndex: best.segmentIndex, hitPoint: best.hitPoint } : null;
}

// Paste a copied obstacle at VARS.mouseNDC (now undo + redo)
export function pasteCopiedObstacle(options = {}) {
    const { setPolygons } = options;
    const data = VARS.copiedObstacle;
    if (!data) return;

    const ndc = VARS.mouseNDC || { x: 0, y: 0 };
    const mouse = new THREE.Vector2(ndc.x, ndc.y);

    // 1️⃣ Raycast from camera through mouse
    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(mouse, sceneManager.camera);

    // 2️⃣ Find which segment is under the mouse
    const segHit = findSegmentUnderRay(raycaster.ray, sceneManager.camera);
    if (!segHit) {
        console.warn(
            "Paste: no segment under mouse – cannot determine parent segment"
        );
        return;
    }

    const parentSegmentIndex = segHit.segmentIndex;
    const pasteCenter = segHit.hitPoint;
    const segment = POLYGONS[parentSegmentIndex];
    if (!segment) return;

    // 3️⃣ Translate original cords so their center moves to pasteCenter
    const plane = createSegmentPlane(parentSegmentIndex);
    const oldCenter = getPointsCenter(data.cords);

    const translatedPointsRaw = data.cords.map((p) => {
        const np = p.clone().sub(oldCenter).add(pasteCenter);
        if (plane) plane.projectPoint(np, np);
        return np;
    });
    const translatedPoints = projectObstaclePointsToRoof(translatedPointsRaw, parentSegmentIndex);

    // 4️⃣ Build shape + mesh + offset like obstacle creation
    const shapePoints = translatedPoints.map(
        (p) => new THREE.Vector2(p.x, p.y)
    );
    const shape = new THREE.Shape(shapePoints);

    const color = data.ogColor || COLORS.red;
    const extrudeMesh = addExtrude(
        translatedPoints,
        shape,
        data.height,
        color,
        sceneManager.scene,
        data.isFilled === true
    );

    const { mesh, shape: finalShape, points, appliedOffset } = addOffset(
        extrudeMesh,
        translatedPoints,
        data.type,
        shape,
        data.offset,
        data.height,
        parentSegmentIndex
    );

    // 5️⃣ Create a brand-new obstacle object (parented to the segment we pasted on)
    const obs = {
        type: data.type,
        polyShape: finalShape,
        cords: points,
        mesh,
        height: data.height,
        ogColor: color,
        offset: typeof appliedOffset === "number" ? appliedOffset : data.offset,
        isFilled: data.isFilled === true,
        parentSegment: parentSegmentIndex,
        linkedSegments: [parentSegmentIndex],
    };

    segment.obstructions.push(obs);
    const obstacleIndex = segment.obstructions.length - 1;
    toast.success("Obstacle pasted");

    // 6️⃣ Update overlaps with other segments
    // 6️⃣ Do NOT auto-attach to intersecting segments until user flushes
obs.isFlushed = false;
obs.linkedSegments = [parentSegmentIndex];


    console.log(
        "📎 Pasted obstacle at segment",
        parentSegmentIndex,
        "index",
        obstacleIndex,
        "linkedSegments:",
        obs.linkedSegments
    );

    // 🔴 Make paste undoable + redoable
    const obstacleRef = obs;
    const cordsSnapshot = obs.cords.map((p) => p.clone());
    const typeSnapshot = obs.type;
    const heightSnapshot = obs.height;
    const offsetSnapshot = obs.offset;
    const isFilledSnapshot = obs.isFilled === true;
    const colorSnapshot = obs.ogColor || COLORS.red;
    const uiSnapshot = { type: typeSnapshot, height: heightSnapshot, offset: offsetSnapshot, isFilled: isFilledSnapshot };
    const uiIndexSnapshot = obstacleIndex;

    pushUndoAction({
        label: "Paste obstacle",
        undo: () => {
            internalDeleteObstacle(obstacleRef);
            if (typeof setPolygons === "function") {
                setPolygons((prev) => {
                    if (!prev || !prev[parentSegmentIndex]) return prev;
                    const next = [...prev];
                    const seg = { ...next[parentSegmentIndex] };
                    const obsList = Array.isArray(seg.obstructions) ? [...seg.obstructions] : [];
                    if (uiIndexSnapshot < 0 || uiIndexSnapshot >= obsList.length) return prev;
                    obsList.splice(uiIndexSnapshot, 1);
                    seg.obstructions = obsList;
                    next[parentSegmentIndex] = seg;
                    return next;
                });
            }
        },
        redo: () => {
            internalRecreateObstacle(
                obstacleRef,
                cordsSnapshot,
                typeSnapshot,
                heightSnapshot,
                offsetSnapshot,
                isFilledSnapshot,
                colorSnapshot,
                parentSegmentIndex
            );
            if (typeof setPolygons === "function") {
                setPolygons((prev) => {
                    if (!prev || !prev[parentSegmentIndex]) return prev;
                    const next = [...prev];
                    const seg = { ...next[parentSegmentIndex] };
                    const obsList = Array.isArray(seg.obstructions) ? [...seg.obstructions] : [];
                    const insertIndex = Math.min(uiIndexSnapshot, obsList.length);
                    obsList.splice(insertIndex, 0, uiSnapshot);
                    seg.obstructions = obsList;
                    next[parentSegmentIndex] = seg;
                    return next;
                });
            }
        },
    });

    // Returning so caller can update SegmentNavigator / selection state
    return { segmentIndex: parentSegmentIndex, obstacleIndex };
}

// ---------------------------------------------------------------------------
// INTERNAL HELPERS FOR DELETE / RECREATE (USED BY DELETE + PASTE UNDO/REDO)
// ---------------------------------------------------------------------------

function internalDeleteObstacle(obstruction) {
    if (!obstruction) return;

    // Remove mesh from scene
    if (obstruction.mesh) {
        const mesh = obstruction.mesh;
        if (mesh.parent) {
            mesh.parent.remove(mesh);
        } else {
            sceneManager.scene.remove(mesh);
        }
        if (mesh.geometry) mesh.geometry.dispose();
        if (mesh.material) mesh.material.dispose();
        obstruction.mesh = null;
    }

    // Remove from all segments that reference it
    POLYGONS.forEach((seg) => {
        if (!seg || !Array.isArray(seg.obstructions)) return;
        seg.obstructions = seg.obstructions.filter((o) => o !== obstruction);
    });

    // Clear linked segments (they'll be recomputed on recreate)
    obstruction.linkedSegments = [];
    notifyIrradianceNeedsRecompute("obstacle-removed");
    refreshPanelOverlapHighlights();
}

/**
 * Rebuild obstacle mesh + offset and re-link it to segments.
 * Used for undo (delete) and redo (paste).
 */
function internalRecreateObstacle(
    obstruction,
    cordsSnapshot,
    typeSnapshot,
    heightSnapshot,
    offsetSnapshot,
    isFilledSnapshot,
    colorSnapshot,
    parentSegmentIndex
) {
    if (!obstruction) return;

    const basePointsRaw = cordsSnapshot.map((p) => p.clone());
    const basePoints = projectObstaclePointsToRoof(basePointsRaw, parentSegmentIndex);

    const shapePoints = basePoints.map(
        (p) => new THREE.Vector2(p.x, p.y)
    );
    const shape = new THREE.Shape(shapePoints);

    const baseMesh = addExtrude(
        basePoints,
        shape,
        heightSnapshot,
        colorSnapshot,
        sceneManager.scene,
        isFilledSnapshot === true
    );

    const { mesh, shape: finalShape, points, appliedOffset } = addOffset(
        baseMesh,
        basePoints,
        typeSnapshot,
        shape,
        offsetSnapshot,
        heightSnapshot,
        parentSegmentIndex
    );

    obstruction.type = typeSnapshot;
    obstruction.height = heightSnapshot;
    obstruction.offset = (typeof appliedOffset === "number") ? appliedOffset : offsetSnapshot;
    obstruction.isFilled = isFilledSnapshot === true;
    obstruction.ogColor = colorSnapshot;
    obstruction.cords = points;
    obstruction.mesh = mesh;
    obstruction.polyShape = finalShape;
    obstruction.parentSegment = parentSegmentIndex;

    if (typeof obstruction.isFlushed !== "boolean") obstruction.isFlushed = false;


    // Recompute linked segments using the shared helper
    updateLinkedSegmentsAfterMove(obstruction);
    notifyIrradianceNeedsRecompute("obstacle-created");
    refreshPanelOverlapHighlights(obstruction.linkedSegments);
}

function getPointsCenter(points) {
    const center = new THREE.Vector3();
    points.forEach((p) => center.add(p));
    center.divideScalar(points.length || 1);
    return center;
}
